import { Icon } from '@iconify/react';
import { useEffect, useState } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { ParameterDto } from '@/Domain/api/Action/dto/Action.dto';
import { IconButton } from '@/Domain/components/Shared/Button/Button';
import { Checkbox } from '@/Domain/components/Shared/CheckBox/Checkbox';
import { FormControl, FormField, FormItem } from '@/Domain/components/Shared/Form/Form';
import { FormMessage } from '@/Domain/components/Shared/FormCustom/FormCustom';
import { Input } from '@/Domain/components/Shared/Input/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/Domain/components/Shared/Select/Select';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { ParameterTypeEnum } from '@/Domain/enum/ParameterType.enum';
import { ActionHelper } from '@/Domain/helper/Action.helper';
import { HttpActionForm } from '@/Domain/hooks/Action/ApiAction/useHttpRequestActionFormV2.hook';

type Props = {
  index: number;
  form: UseFormReturn<HttpActionForm>;
  remove: (index: number) => void;
};

export const AiParameterItem = ({ form, index, remove }: Props) => {
  const [t] = useTranslation('actions_page');
  const [isUniqueName, setIsUniqueName] = useState(true);

  const parameters = useWatch({ name: 'parameters' });
  const allNames = parameters.map((param: ParameterDto) => param.name).filter(Boolean);

  const data = useWatch({ name: `parameters.${index}` });
  const isBoolType = data.type === ParameterTypeEnum.Boolean;

  const handleDeleteParameterForm = () => remove(index);

  useEffect(() => {
    const isUnique = allNames.filter((name: string) => name === data.name).length > 1;

    setIsUniqueName(!isUnique);
  }, [allNames, data.name]);

  return (
    <section
      className="grid grid-cols-[175px_minmax(100px,0.5fr)_minmax(125px,1fr)_minmax(100px,0.5fr)_minmax(60px,0.2fr)_auto_auto]
 p-4 items-start gap-4"
    >
      <FormField
        name={`parameters.${index}.type`}
        render={({ field }) => (
          <FormItem className="w-full min-w-0">
            <FormControl>
              <Select
                value={field.value}
                onValueChange={(value) => {
                  if (value === ParameterTypeEnum.Boolean) {
                    form.setValue(`parameters.${index}.default_value`, '0');
                  } else {
                    const defaultValue =
                      form.formState.defaultValues?.parameters?.[index]?.default_value;

                    form.setValue(`parameters.${index}.default_value`, defaultValue || '');
                  }
                  field.onChange(value);
                }}
              >
                <SelectTrigger className="w-full truncate min-w-0" size="sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ParameterTypeEnum).map(([key, value]) => {
                    const { TypeIcon, label } = ActionHelper.parameterTypeToLabel(value);

                    return (
                      <SelectItem key={key} value={value}>
                        <div className="flex items-center gap-2">
                          <TypeIcon className="text-foreground-subtle size-4" />
                          {label}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </FormControl>
          </FormItem>
        )}
      />

      <FormField
        name={`parameters.${index}.name`}
        render={({ field, fieldState }) => {
          const errorMessage = isUniqueName
            ? undefined
            : {
                type: z.ZodIssueCode.custom,
                message: t('sheet.form.validation.parameter_name_unique'),
              };

          return (
            <FormItem className="w-full min-w-0">
              <FormControl>
                <Input
                  size="sm"
                  placeholder={t('sheet.form.data_to_collect.table.name_placeholder')}
                  error={!!fieldState.error || !!errorMessage}
                  {...field}
                />
              </FormControl>
              <FormMessage error={fieldState.error || errorMessage} />
            </FormItem>
          );
        }}
      />

      <FormField
        name={`parameters.${index}.description`}
        render={({ field, fieldState }) => (
          <FormItem className="w-full min-w-0">
            <FormControl>
              <Input
                size="sm"
                placeholder={t('sheet.form.data_to_collect.table.description_placeholder')}
                error={!!fieldState.error}
                {...field}
              />
            </FormControl>
            <FormMessage error={fieldState.error} />
          </FormItem>
        )}
      />

      <FormField
        name={`parameters.${index}.default_value`}
        render={({ field }) => (
          <FormItem className="w-full min-w-0">
            {isBoolType ? (
              <FormControl>
                <Select
                  defaultValue="0"
                  value={field.value ? field.value : '0'}
                  onValueChange={field.onChange}
                >
                  <SelectTrigger className="w-full" size="sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">True</SelectItem>
                    <SelectItem value="0">False</SelectItem>
                  </SelectContent>
                </Select>
              </FormControl>
            ) : (
              <FormControl>
                <Input
                  size="sm"
                  placeholder={t('sheet.form.data_to_collect.table.default_value_placeholder')}
                  {...field}
                />
              </FormControl>
            )}

            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        name={`parameters.${index}.is_required`}
        render={({ field }) => (
          <FormItem className="flex items-center justify-center py-2">
            <FormControl>
              <Checkbox checked={field.value} onCheckedChange={(check) => field.onChange(check)} />
            </FormControl>
          </FormItem>
        )}
      />

      <Separator orientation="vertical" />

      <Tooltip>
        <TooltipTrigger asChild>
          <IconButton
            variant="secondary"
            size="sm"
            className="hover:!text-red-500 z-40"
            onClick={handleDeleteParameterForm}
          >
            <Icon icon="mingcute:delete-line" />
          </IconButton>
        </TooltipTrigger>
        <TooltipContent>{t('tooltip.delete')}</TooltipContent>
      </Tooltip>
    </section>
  );
};
